import React from 'react';
import { useAuth } from '../context/AuthContext';

// Fixes AUDIT_REPORT M3: cell values (customer names, notes, addresses)
// were previously written into the popup document via document.write()
// completely unescaped, so any field containing '<' or '>' would be parsed
// as markup in that window. Escaping here doesn't change what's displayed
// for normal text, only how special characters are rendered.
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function ExportButton({ apiUrl, filename }) {
  const { user } = useAuth();
  if (!user || user.role !== 'admin') return null;

  function getBackendBase() {
    // Fixes AUDIT_REPORT M5: reuse the same base-URL resolution as api.js
    // (REACT_APP_API_URL in production) instead of a second hardcoded
    // http://<host>:5000 copy that breaks behind HTTPS/a reverse proxy.
    // window.UPLOAD_BASE is legitimately an empty string when the frontend
    // and API share the same origin (see api.js's resolveApiBase()), so we
    // must check for undefined/null specifically rather than falsiness --
    // `window.UPLOAD_BASE || fallback` was skipping right past a valid ""
    // and guessing a nonexistent :5000 host in production.
    return (window.UPLOAD_BASE !== undefined && window.UPLOAD_BASE !== null)
      ? window.UPLOAD_BASE
      : (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'http://' + window.location.hostname + ':5000');
  }

  // Every /api route is behind authenticateToken, which reads the JWT from
  // an Authorization header (see api.js's request interceptor) -- there's
  // no cookie-based session at all. Both export actions previously reached
  // the backend with no Authorization header whatsoever: handleCSV used
  // window.open() (a plain browser navigation, which can never carry a
  // custom header), and handlePDF's fetch() call simply never attached one.
  // That's why CSV showed "Authentication required" and PDF's fetch got
  // back a one-line JSON error body instead of CSV text, which looked like
  // "no data" once split on '\n'. Fixed by explicitly attaching the same
  // Bearer token api.js uses, and switching CSV to fetch+blob (a plain
  // window.open can't send headers, so it's the only way to authenticate a
  // file download without exposing the token in the URL).
  function authedFetch(url) {
    var token = localStorage.getItem('token');
    return fetch(url, { headers: token ? { Authorization: 'Bearer ' + token } : {} });
  }

  function handleCSV() {
    var base = getBackendBase();
    var sep = apiUrl.indexOf('?') !== -1 ? '&' : '?';
    var url = base + apiUrl + sep + 'export=csv';

    authedFetch(url)
      .then(function(response) {
        if (!response.ok) { throw new Error(response.status === 401 ? 'Authentication required -- please log in again.' : 'Export failed (' + response.status + ')'); }
        return response.blob();
      })
      .then(function(blob) {
        var link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename || 'export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(function(err) { alert(err.message || 'Failed to download CSV.'); });
  }

  function handlePDF() {
    var base = getBackendBase();
    var sep = apiUrl.indexOf('?') !== -1 ? '&' : '?';
    var url = base + apiUrl + sep + 'export=csv';

    authedFetch(url)
      .then(function(response) {
        if (!response.ok) { throw new Error(response.status === 401 ? 'Authentication required -- please log in again.' : 'Export failed (' + response.status + ')'); }
        return response.text();
      })
      .then(function(csv) {
        var lines = csv.split('\n');
        if (lines.length < 2) {
          alert('No data to export');
          return;
        }
        var headers = lines[0].split(',').map(function(h) { return h.replace(/"/g, ''); });
        var rows = lines.slice(1).filter(function(l) { return l.trim(); }).map(function(line) {
          return line.split(',').map(function(cell) { return cell.replace(/"/g, ''); });
        });

        var printWindow = window.open('', '_blank', 'width=900,height=600');
        printWindow.document.write('<html><head><title>Export</title><style>');
        printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
        printWindow.document.write('table { border-collapse: collapse; width: 100%; font-size: 12px; }');
        printWindow.document.write('th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }');
        printWindow.document.write('th { background-color: #f2f2f2; font-weight: bold; }');
        printWindow.document.write('@media print { button { display: none; } }');
        printWindow.document.write('</style></head><body>');
        printWindow.document.write('<h2>Export Data</h2>');
        printWindow.document.write('<button onclick="window.print()">Print / Save as PDF</button>');
        printWindow.document.write('<table><thead><tr>');
        headers.forEach(function(h) { printWindow.document.write('<th>' + escapeHtml(h) + '</th>'); });
        printWindow.document.write('</tr></thead><tbody>');
        rows.forEach(function(row) {
          printWindow.document.write('<tr>');
          row.forEach(function(cell) { printWindow.document.write('<td>' + escapeHtml(cell) + '</td>'); });
          printWindow.document.write('</tr>');
        });
        printWindow.document.write('</tbody></table></body></html>');
        printWindow.document.close();
      })
      .catch(function(err) {
        alert(err.message || 'Failed to load data for PDF export. Please try again.');
      });
  }

  return React.createElement('div', { style: { display: 'inline-flex', gap: 6 } },
    React.createElement('button', {
      onClick: handleCSV,
      title: 'Download CSV (opens in Excel)',
      style: { background: '#16a34a', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 }
    }, '\u{1F4E5} CSV'),
    React.createElement('button', {
      onClick: handlePDF,
      title: 'Open as printable table',
      style: { background: '#dc2626', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 }
    }, '\u{1F5A8}\u{FE0F} PDF')
  );
}
